import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Anchor, Loader2, Plus, Trash2, Save, Ticket, ClipboardList, Check, X, Ship,
} from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { i: 0, label: "Mandag" }, { i: 1, label: "Tirsdag" },
  { i: 2, label: "Onsdag" }, { i: 3, label: "Torsdag" },
  { i: 4, label: "Fredag" }, { i: 5, label: "Lørdag" },
  { i: 6, label: "Søndag" },
];
const STATUS_LABEL = {
  pending_review: "Venter godkjenning",
  awaiting_payment: "Venter betaling",
  paid: "Betalt",
  completed: "Gjennomført",
  cancelled: "Avlyst",
  rejected: "Avvist",
};
const STATUS_COLOR = {
  pending_review: "bg-amber-100 text-amber-800",
  awaiting_payment: "bg-slate-100 text-slate-800",
  paid: "bg-emerald-100 text-emerald-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800",
};

function fmtMoney(v, cur) {
  return `${Number(v || 0).toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${cur === "NOK" ? "kr" : cur === "EUR" ? "€" : cur}`;
}

export default function BoatLiftAdmin() {
  return (
    <div className="space-y-5 sm:space-y-6" data-testid="lift-admin-page">
      <div>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.32em] text-[#A57F39]">
          Løftetjenesten
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Løft av privat båt</h1>
        <p className="text-xs sm:text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Åpne eller steng tjenesten, juster pris og åpningstider, håndter
          rabattkoder og se alle bookinger.
        </p>
      </div>

      <Tabs defaultValue="config">
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="config" data-testid="lift-tab-config">Innstillinger</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="lift-tab-bookings">Bookinger</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="lift-tab-coupons">Rabattkoder</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="config" className="mt-5"><ConfigPanel /></TabsContent>
        <TabsContent value="bookings" className="mt-5"><BookingsPanel /></TabsContent>
        <TabsContent value="coupons" className="mt-5"><CouponsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/lift/config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch("/lift/config", patch);
      setCfg(r.data);
      toast.success("Lagret");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setBusy(false);
    }
  };
  if (!cfg) return <Card className="p-10 flex items-center justify-center text-[#A57F39]"><Loader2 className="w-5 h-5 animate-spin" /></Card>;
  return (
    <div className="space-y-5">
      <Card className={`p-4 sm:p-5 ${cfg.enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Anchor className="w-5 h-5 text-[#A57F39]" />
              <div className="text-lg font-bold text-[#0C1824]">
                {cfg.enabled ? "Løftetjenesten er ÅPEN" : "Løftetjenesten er STENGT"}
              </div>
            </div>
            <div className="text-xs text-[#2B3F4D] mt-1">
              {cfg.enabled
                ? "Kunder kan booke og betale på canalboats.me/loft."
                : "Kunder ser en «midlertidig stengt»-melding på siden."}
            </div>
          </div>
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => save({ enabled: v })}
            data-testid="lift-cfg-enabled"
            disabled={busy}
          />
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-[#0C1824]">Pris & vekt</h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Fast pris (inkl. mva)</Label>
            <Input type="number" step="0.01" data-testid="lift-cfg-price"
              defaultValue={cfg.price_gross}
              onBlur={(e) => save({ price_gross: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-xs">Valuta</Label>
            <Select value={cfg.currency} onValueChange={(v) => save({ currency: v })}>
              <SelectTrigger data-testid="lift-cfg-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NOK">NOK</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">MVA-sats (%)</Label>
            <Input type="number" step="0.5" defaultValue={cfg.vat_rate}
              onBlur={(e) => save({ vat_rate: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-xs">Maks vekt (t)</Label>
            <Input type="number" step="0.1" defaultValue={cfg.max_weight_tons}
              onBlur={(e) => save({ max_weight_tons: Number(e.target.value) })} />
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-[#0C1824]">Åpningstider</h3>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Åpner</Label>
            <Input type="time" defaultValue={cfg.open_from} data-testid="lift-cfg-from"
              onBlur={(e) => save({ open_from: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Stenger</Label>
            <Input type="time" defaultValue={cfg.open_to} data-testid="lift-cfg-to"
              onBlur={(e) => save({ open_to: e.target.value })} />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs">Dager tjenesten er åpen</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const on = cfg.days_open.includes(d.i);
              return (
                <button
                  key={d.i}
                  type="button"
                  onClick={() => {
                    const next = on
                      ? cfg.days_open.filter((x) => x !== d.i)
                      : [...cfg.days_open, d.i].sort();
                    save({ days_open: next });
                  }}
                  className={`px-3 py-2 rounded-md border-2 text-sm ${
                    on ? "border-[#BD984D] bg-[#BD984D]/10 text-[#0C1824] font-semibold"
                       : "border-slate-200 text-slate-500"
                  }`}
                  data-testid={`lift-cfg-day-${d.i}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-4 flex items-start gap-2">
          <Switch checked={cfg.allow_outside_hours_requests}
                  onCheckedChange={(v) => save({ allow_outside_hours_requests: v })}
                  data-testid="lift-cfg-outside" />
          <div>
            <div className="text-sm font-semibold text-[#0C1824]">Tillat forespørsler utenom åpningstid</div>
            <div className="text-xs text-[#2B3F4D]">Kunden får «venter godkjenning» og du bekrefter først, deretter faktureres.</div>
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-[#0C1824]">Offentlige tekster</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Beskrivelse (NO)</Label>
            <Textarea rows={3} defaultValue={cfg.public_notes_no}
              onBlur={(e) => save({ public_notes_no: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Beskrivelse (EN)</Label>
            <Textarea rows={3} defaultValue={cfg.public_notes_en}
              onBlur={(e) => save({ public_notes_en: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Stengt-melding (NO)</Label>
            <Input defaultValue={cfg.closed_notice_no}
              onBlur={(e) => save({ closed_notice_no: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Stengt-melding (EN)</Label>
            <Input defaultValue={cfg.closed_notice_en}
              onBlur={(e) => save({ closed_notice_en: e.target.value })} />
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function BookingsPanel() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const params = statusFilter && statusFilter !== "__all__" ? { status: statusFilter } : {};
      const r = await api.get("/lift/bookings", { params });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste");
      setItems([]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/lift/bookings/${id}`, { status: newStatus });
      toast.success("Oppdatert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Slette denne bookingen?")) return;
    try {
      await api.delete(`/lift/bookings/${id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:flex sm:items-end gap-2 sm:gap-3">
          <div className="sm:w-56">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="lift-bookings-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle statuser</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {busy ? (
        <Card className="p-10 flex items-center justify-center text-[#A57F39]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-[#2B3F4D]" data-testid="lift-bookings-empty">
          Ingen bookinger for valgt status.
        </Card>
      ) : (
        <div className="space-y-2" data-testid="lift-bookings-list">
          {items.map((b) => (
            <Card key={b.id} className="p-3 sm:p-4" data-testid={`lift-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${STATUS_COLOR[b.status] || "bg-slate-100"} border-0 text-[10px]`}>
                      {STATUS_LABEL[b.status] || b.status}
                    </Badge>
                    <span className="text-xs text-[#2B3F4D]">{(b.requested_start_at || "").slice(0, 16).replace("T", " ")}</span>
                    {b.within_open_hours === false && (
                      <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Utenom åpningstid</Badge>
                    )}
                  </div>
                  <div className="mt-1.5 font-semibold text-[#0C1824]">
                    {b.customer_name} <span className="text-xs text-[#2B3F4D] font-normal">· {b.customer_phone}</span>
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-0.5 break-words">
                    <Ship className="w-3 h-3 inline mr-1" />
                    {b.boat_name} · {b.boat_weight_tons} t
                    {b.boat_length_m ? ` · ${b.boat_length_m} m` : ""}
                    {" · "}
                    {b.hours_up} t oppe
                  </div>
                  {b.reason_for_lift && (
                    <div className="text-xs text-[#0C1824] mt-1 italic">«{b.reason_for_lift}»</div>
                  )}
                  {b.coupon_code && (
                    <div className="text-[11px] text-[#A57F39] mt-0.5">
                      <Ticket className="w-3 h-3 inline mr-1" />
                      {b.coupon_code} · −{fmtMoney(b.discount_amount, b.currency)}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-[#0C1824]">{fmtMoney(b.final_amount, b.currency)}</div>
                  {b.paid_at && <div className="text-[11px] text-emerald-700 mt-0.5">Betalt {b.paid_at.slice(0, 10)}</div>}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {b.status === "pending_review" && (
                  <>
                    <Button size="sm" onClick={() => updateStatus(b.id, "awaiting_payment")}
                            data-testid={`lift-approve-${b.id}`}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="w-4 h-4 mr-1" /> Godkjenn (send betaling)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "rejected")}
                            className="border-red-300 text-red-700">
                      <X className="w-4 h-4 mr-1" /> Avvis
                    </Button>
                  </>
                )}
                {b.status === "awaiting_payment" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "paid")}
                          className="border-emerald-300 text-emerald-700">
                    Marker som betalt (utenom Stripe)
                  </Button>
                )}
                {b.status === "paid" && (
                  <Button size="sm" onClick={() => updateStatus(b.id, "completed")}
                          className="bg-[#0C1824] hover:bg-[#152238] text-white">
                    Marker som gjennomført
                  </Button>
                )}
                {["pending_review", "awaiting_payment"].includes(b.status) && (
                  <Button size="sm" variant="ghost" onClick={() => updateStatus(b.id, "cancelled")}
                          className="text-slate-500">
                    Avlys
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(b.id)} className="text-red-500 ml-auto">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function CouponsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", label: "", discount_type: "percent", discount_value: 10,
    active: true, valid_from: "", valid_to: "", usage_limit: "",
  });
  const load = async () => {
    try {
      const r = await api.get("/lift/coupons");
      setItems(r.data || []);
    } catch (e) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        ...form,
        discount_value: Number(form.discount_value),
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
      };
      await api.post("/lift/coupons", body);
      toast.success("Kupong opprettet");
      setOpen(false);
      setForm({ code: "", label: "", discount_type: "percent", discount_value: 10,
        active: true, valid_from: "", valid_to: "", usage_limit: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette");
    }
  };
  const toggle = async (c) => {
    try {
      await api.patch(`/lift/coupons/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error("Feil"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Slette kupongen ${c.code}?`)) return;
    try { await api.delete(`/lift/coupons/${c.id}`); load(); }
    catch { toast.error("Feil"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-2">
        <p className="text-sm text-[#2B3F4D] max-w-xl">
          Rabattkoder for båtforeninger og kampanjer. Kunder skriver koden i booking-skjemaet
          på løft-siden.
        </p>
        <Button onClick={() => setOpen(true)} data-testid="lift-coupon-add"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Ny kupong
        </Button>
      </div>
      {items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-[#2B3F4D]">Ingen kuponger enda.</Card>
      ) : (
        <div className="space-y-2" data-testid="lift-coupons-list">
          {items.map((c) => (
            <Card key={c.id} className="p-3 sm:p-4" data-testid={`lift-coupon-${c.code}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#0C1824] text-white border-0 font-mono">{c.code}</Badge>
                    {c.active ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Aktiv</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-700 border-0 text-[10px]">Deaktivert</Badge>
                    )}
                  </div>
                  <div className="mt-1 font-semibold text-[#0C1824]">{c.label}</div>
                  <div className="text-xs text-[#2B3F4D] mt-0.5">
                    {c.discount_type === "percent" ? `${c.discount_value}% rabatt` : `${c.discount_value} kr rabatt`}
                    {" · brukt "}{c.times_used || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""} ganger
                    {c.valid_from && ` · fra ${c.valid_from}`}
                    {c.valid_to && ` · til ${c.valid_to}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Switch checked={c.active} onCheckedChange={() => toggle(c)} />
                  <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Ny rabattkode</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Kode</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     className="uppercase" placeholder="F.eks. SKIENBF" data-testid="lift-coupon-code" />
            </div>
            <div className="col-span-2">
              <Label>Beskrivelse</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder="Skien Båtforening" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Prosent (%)</SelectItem>
                  <SelectItem value="fixed">Fast beløp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Verdi</Label>
              <Input type="number" step="0.01" value={form.discount_value}
                     onChange={(e) => setForm({ ...form, discount_value: e.target.value })} />
            </div>
            <div>
              <Label>Gyldig fra</Label>
              <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
            </div>
            <div>
              <Label>Gyldig til</Label>
              <Input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Maks antall bruk (valgfritt)</Label>
              <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button onClick={save} className="bg-[#BD984D] hover:bg-[#A57F39] text-white" data-testid="lift-coupon-save">
              <Save className="w-4 h-4 mr-1.5" /> Lagre
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
